// Copyright 2018 Sorama B.V.

import { autoinject, observable, transient } from "aurelia-framework";
import { Quad } from "datatypes/Generic/Quad";
import { GenericMeasurement, TargetAndSourceRegionLinkProperties } from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { URA } from "libraries/Math/URA";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { Image } from "services/Image/IImageService";

@transient()
@autoinject
export class ImagePickerModal implements IModal<ImagePickerModalState> {
    public onSelection: (image: Image) => void;
    public onEdit: (image: Image) => void;

    @observable
    public selectedImage: Image;
    public measurement: GenericMeasurement;

    private state: ImagePickerModalState;
    private deviceAspectRatio: number;

    public lastUploadedImageId?: string;

    activateModal(
        state: ImagePickerModalState,
        modalInstance: IWorkflowModuleModalInstance<ImagePickerModalState>
    ): void {
        this.selectedImage = state.selectedImage;
        this.measurement = state.measurement;
        this.state = state;

        // Get microphone positions for measurement
        const microphonePositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(this.measurement);

        // Get URA properties
        const uraProperties = URA.getUraProperties3D(microphonePositions);

        if (uraProperties.isUra) {
            // Device is an ura, easy to get an aspect ratio
            this.deviceAspectRatio = uraProperties.width / uraProperties.height;
        } else {
            // Device is not an ura.. unclear what the region should be, use 1 for now
            this.deviceAspectRatio = 1;
        }
    }

    deactivate(): ImagePickerModalState {
        return this.state;
    }

    public selectedImageChanged(): void {
        if (!this.deviceAspectRatio) return; // Not yet initialized

        if (this.state.onSelection) {
            if (this.selectedImage) {
                const existingLink = this.measurement.links.find((x) => x.mediaObjectId === this.selectedImage.id);
                // Determine region
                const imageRegion = existingLink
                    ? Quad.clone((<TargetAndSourceRegionLinkProperties>existingLink.properties).sourceRegion)
                    : Quad.scale(
                          Quad.fitQuadOverArea(this.deviceAspectRatio, this.selectedImage.dimension),
                          1 / this.selectedImage.dimension.width,
                          1 / this.selectedImage.dimension.height
                      );

                this.state.onSelection(this.selectedImage, imageRegion);

                if (this.lastUploadedImageId == this.selectedImage.id) {
                    this.state.onEdit(this.selectedImage, imageRegion);
                }
            } else {
                this.state.onSelection(undefined, undefined);
            }
        }

        this.state.selectedImage = this.selectedImage;
    }
}

export interface ImagePickerModalState {
    measurement: GenericMeasurement;

    onSelection?: (image: Image, region: Quad) => void;
    selectedImage?: Image;
    onEdit?: (image: Image, region: Quad) => void;
}
